/**
 * Wires "Scroll link" blocks (.pdp-scroll-link[data-scroll-link]) on the PDP:
 * opens the product accordion whose handleized title matches the link's
 * data-scroll-target via the `accordion:toggle` CustomEvent, then smooth-
 * scrolls to it. Opening first means the accordion's full height is known
 * when the scroll target is measured, so the scroll lands accurately.
 *
 * Loaded by layout/theme.liquid on product pages, and by
 * snippets/deferred-scripts.liquid on Medispa LP pages (marker: data-scroll-link).
 */

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest<HTMLElement>('[data-scroll-link]');
  if (!link) return;

  event.preventDefault();

  const title = link.getAttribute('data-scroll-target');
  if (!title) return;

  const target = document.querySelector<HTMLElement>(
    `.js-product-accordion-item[data-accordion-title="${CSS.escape(title)}"]`,
  );
  if (!target) return;

  // Close all accordions first, then open the target. Wait for the expand
  // transition (400ms CSS height transition) to finish before measuring
  // the scroll target so the scroll lands accurately on the fully expanded
  // content.
  document.dispatchEvent(
    new CustomEvent('accordion:toggle', { detail: { action: 'close-all' } }),
  );
  document.dispatchEvent(
    new CustomEvent('accordion:toggle', { detail: { title, action: 'open' } }),
  );

  const scrollToTarget = (): void => {
    // Offset by the sticky header height so the accordion isn't hidden behind it.
    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
      ) || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;

    window.scrollTo({ top, behavior: 'smooth' });
  };

  // The expand is a CSS height transition on .product-accordion__content.
  // Wait for it to finish before measuring. Fall back to a timer if the
  // transitionend event doesn't fire (e.g. content height is 0).
  const content = target.querySelector<HTMLElement>('.js-product-accordion-content');
  let scrolled = false;

  const onTransitionEnd = (): void => {
    if (scrolled) return;
    scrolled = true;
    scrollToTarget();
  };

  if (content) {
    content.addEventListener('transitionend', onTransitionEnd, { once: true });
  }

  // Safety fallback: if transitionend never fires, scroll after the
  // 400ms transition duration plus a small buffer.
  setTimeout(onTransitionEnd, 450);
});
